// ---------------------------------------------------------------------------
// HDDN LAB marketing site — route table.
//
// The booking app routes by matching window.location.pathname through a long
// if/else chain. That chain is load-bearing for booking and admin, which are out
// of scope here, so it is left alone: the marketing site adds one matcher and
// one early return.
//
// Client-side navigation pushes state and then dispatches `popstate`, because
// the host app already listens for `popstate` to re-run its own route handler.
// That one line is what lets these pages navigate without touching the chain.
// ---------------------------------------------------------------------------

import { createElement } from 'react';
import Home from './pages/Home.jsx';
import Content from './pages/Content.jsx';
import Studio from './pages/Studio.jsx';
import Work from './pages/Work.jsx';
import About from './pages/About.jsx';
import Contact from './pages/Contact.jsx';
import Photographer from './pages/Photographer.jsx';
import Careers from './pages/Careers.jsx';
import Job from './pages/Job.jsx';
import { jobs } from './jobs.json';
import meta from './meta.json';
import { crossHostUrl } from '../hosts.js';

// About lives in the footer: with three doors in the nav, five links is the
// most that fits beside the wordmark and the availability button at 940px.
export const NAV_LINKS = [
  { href: '/content', label: 'Content creation' },
  { href: '/photographer', label: 'Photographer' },
  { href: '/studio', label: 'The studio' },
  { href: '/work', label: 'Work' },
  { href: '/contact', label: 'Contact' },
];

const PAGES = {
  '/': Home,
  '/content': Content,
  '/studio': Studio,
  '/work': Work,
  '/about': About,
  '/contact': Contact,
  '/photographer': Photographer,
  '/careers': Careers,
  // One page per open role, bound to its slug so Job needs no props from the host.
  ...Object.fromEntries(jobs.map((j) => [`/careers/${j.slug}`, () => createElement(Job, { slug: j.slug })])),
};

// /gallery was the old portfolio URL and is indexed; keep it working.
export const REDIRECTS = {
  '/gallery': '/work',
};

export function normalizePath(pathname) {
  if (!pathname) return '/';
  const p = pathname.replace(/\/+$/, '');
  return p === '' ? '/' : p;
}

export function matchSiteRoute(pathname) {
  const path = normalizePath(pathname);
  const component = PAGES[path];
  if (!component) return null;
  return { path, component, meta: meta.routes[path] || meta.fallback };
}

export function isRedirect(pathname) {
  return REDIRECTS[normalizePath(pathname)] || null;
}

export function navigate(href) {
  if (typeof window === 'undefined') return;
  // The admin lives on its own hostname. A pushState cannot cross hosts, so a
  // link that belongs to the other one becomes a real navigation instead.
  const elsewhere = crossHostUrl(href);
  if (elsewhere) { window.location.href = elsewhere; return; }
  window.history.pushState({}, '', href);
  // The host app's route handler is bound to popstate; pushState alone does not
  // fire it. Dispatching keeps navigation working without editing that handler.
  window.dispatchEvent(new PopStateEvent('popstate'));

  // A cross-page anchor (/content#one-off) has to wait for the target page to
  // render before it can be scrolled to; jumping to the top first would be
  // wrong, and scrolling immediately would find nothing.
  const hash = href.includes('#') ? href.slice(href.indexOf('#') + 1) : '';
  if (hash) {
    requestAnimationFrame(() => {
      const target = document.getElementById(hash);
      if (target) target.scrollIntoView({ block: 'start' });
      else window.scrollTo({ top: 0, behavior: 'auto' });
    });
    return;
  }
  window.scrollTo({ top: 0, behavior: 'auto' });
}

// onClick factory for internal links. Leaves modified clicks alone so
// cmd-click, middle-click and "open in new tab" still behave normally.
export function go(href) {
  return (e) => {
    if (!e || e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
    e.preventDefault();
    navigate(href);
  };
}
